import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';

interface University {
  name: string;
  logoUrl: string;
}

interface UniversityCarouselProps {
  universities: University[];
  autoplay?: boolean;
  duration?: number;
}

export const UniversityCarousel: React.FC<UniversityCarouselProps> = ({ 
  universities, 
  autoplay = true, 
  duration = 3000 
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleUniversities, setVisibleUniversities] = useState<University[]>([]);

  // Group universities into sets of 4 for display
  const universitiesPerSlide = 4;
  const totalSlides = Math.ceil(universities.length / universitiesPerSlide);

  useEffect(() => {
    const startIndex = currentIndex * universitiesPerSlide;
    const endIndex = startIndex + universitiesPerSlide;
    setVisibleUniversities(universities.slice(startIndex, endIndex));
  }, [currentIndex, universities]);

  useEffect(() => {
    if (autoplay && totalSlides > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % totalSlides);
      }, duration);
      return () => clearInterval(interval);
    }
  }, [autoplay, totalSlides, duration]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % totalSlides);
  };

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + totalSlides) % totalSlides);
  };

  return (
    <div className="relative">
      {/* Universities Grid */}
      <motion.div
        key={currentIndex}
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -50 }}
        transition={{ duration: 0.5 }}
        className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 min-h-[120px]"
      >
        {visibleUniversities.map((university, index) => (
          <motion.div
            key={`${currentIndex}-${index}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1, duration: 0.5 }}
            className="text-center group hover:scale-105 transition-transform duration-300 p-4"
          >
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-4 mx-auto group-hover:shadow-lg transition-shadow duration-300 overflow-hidden border border-gray-200">
              <img 
                src={university.logoUrl} 
                alt={`شعار ${university.name}`}
                className="w-12 h-12 object-contain"
                loading="lazy"
              />
            </div>
            <h4 className="font-semibold text-sm text-government-navy">
              {university.name}
            </h4>
          </motion.div>
        ))}
      </motion.div>

      {/* Navigation Dots */}
      {totalSlides > 1 && (
        <div className="flex justify-center mt-8 gap-2">
          {Array.from({ length: totalSlides }).map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                currentIndex === index
                  ? 'bg-brand-primary shadow-lg'
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`الانتقال للصفحة ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Navigation Arrows */}
      {totalSlides > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4 w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center text-government-navy hover:bg-brand-primary hover:text-white transition-all duration-300"
            aria-label="الجامعة السابقة"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4 w-10 h-10 bg-white shadow-lg rounded-full flex items-center justify-center text-government-navy hover:bg-brand-primary hover:text-white transition-all duration-300"
            aria-label="الجامعة التالية"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}
    </div>
  );
};